import React from 'react';
import styled, { css } from 'styled-components';

// Define theme variants
const VARIANTS = {
  info: {
    bg: '#eff6ff',
    hoverBg: '#dbeafe',
    color: '#0c5460', // Bootstrap info text
    iconColor: '#3b82f6',
    border: '1px solid #bfdbfe',
    icon: 'bx bx-info-circle',
    fontWeight: '400',
  },
  success: {
    bg: '#f0fdf4',
    hoverBg: '#dcfce7',
    color: '#0a3622', // Bootstrap success text
    iconColor: '#22c55e',
    border: '1px solid #bbf7d0',
    icon: 'bx bx-check-circle',
    fontWeight: '400',
  },
  warning: {
    bg: '#fffbeb',
    hoverBg: '#fef3c7',
    color: '#664d03',
    iconColor: '#f59e0b',
    border: '1px solid #fde68a',
    icon: 'bx bx-error',
    fontWeight: '400',
  },
  danger: {
    bg: '#fef2f2',
    hoverBg: '#fee2e2',
    color: '#dc3545',
    iconColor: '#ef4444',
    border: '1px solid #fecaca',
    icon: 'bx bx-error-circle',
    fontWeight: '400',
  },
  default: {
    bg: '#f1f5f9',
    hoverBg: '#e2e8f0',
    color: '#475569',
    iconColor: '#64748b',
    border: 'none',
    icon: 'bx bx-info-circle',
    fontWeight: '400',
  },
  dark: {
    bg: '#1e293b',
    hoverBg: '#334155',
    color: '#e2e8f0',
    iconColor: '#94a3b8',
    border: '1px solid #475569',
    icon: 'bx bx-info-circle',
    fontWeight: '400',
  },
  purple: {
    bg: '#f5f3ff',
    hoverBg: '#ede9fe',
    color: '#5b21b6',
    iconColor: '#8b5cf6',
    border: '1px solid #ddd6fe',
    icon: 'bx bx-bulb',
    fontWeight: '400',
  },
};

// Size variants
const SIZES = {
  sm: {
    padding: '4px 10px',
    fontSize: '12px',
    iconSize: '14px',
    marginTop: '4px',
    rounded: '4px',
  },
  md: {
    padding: '8px 14px',
    fontSize: '13px',
    iconSize: '16px',
    marginTop: '6px',
    rounded: '6px',
  },
  lg: {
    padding: '12px 20px',
    fontSize: '15px',
    iconSize: '20px',
    marginTop: '8px',
    rounded: '8px',
  },
  xl: {
    padding: '16px 24px',
    fontSize: '16px',
    iconSize: '24px',
    marginTop: '10px',
    rounded: '10px',
  },
};

const HelpTextWrapper = styled.div`
  display: flex;
  align-items: ${({ align }) => align || 'center'};
  gap: ${({ gap }) => gap || '4px'};
  margin-top: ${({ marginTop }) => marginTop || '6px'};
  padding: ${({ padding, noBackground }) =>
    noBackground ? '6px 0' : padding || '8px 14px'};
  background: ${({ bg, noBackground }) =>
    noBackground ? 'transparent' : bg || '#f1f5f9'};
  border-radius: ${({ rounded }) => rounded || '6px'};
  font-size: ${({ fontSize }) => fontSize || '13px'};
  color: ${({ color }) => color || '#475569'};
  cursor: ${({ cursor }) => cursor || 'pointer'};
  transition: all 0.25s ease;
  border: ${({ border, noBackground }) =>
    noBackground ? 'none' : border || 'none'};
  box-shadow: ${({ shadow, noBackground }) =>
    noBackground ? 'none' : shadow || 'none'};
  line-height: ${({ lineHeight }) => lineHeight || '1.5'};
  font-weight: ${({ fontWeight }) => fontWeight || '400'};
  flex-shrink: ${({ noShrink }) => (noShrink ? '0' : '1')};

  /* Width controls */
  width: ${({ width }) => width || 'auto'};
  max-width: ${({ maxWidth }) => maxWidth || '100%'};
  min-width: ${({ minWidth }) => minWidth || 'auto'};

  /* Left border - only if not noBackground */
  ${({ showLeftBorder, leftBorderWidth, leftBorderColor, noBackground }) =>
    showLeftBorder &&
    !noBackground &&
    css`
      border-left: ${leftBorderWidth || '3px'} solid
        ${leftBorderColor || '#667eea'};
    `}

  &:hover {
    background: ${({ hoverBg, noBackground }) =>
      noBackground ? 'transparent' : hoverBg || '#e2e8f0'};
    transform: ${({ hoverTransform }) => hoverTransform || 'none'};
    box-shadow: ${({ hoverShadow, noBackground }) =>
      noBackground ? 'none' : hoverShadow || 'none'};
  }

  i {
    color: ${({ iconColor }) => iconColor || '#64748b'};
    font-size: ${({ iconSize }) => iconSize || '16px'};
    flex-shrink: 0;
    margin-right: ${({ iconMarginRight }) => iconMarginRight || '0'};
    transition: transform 0.25s ease;
    display: ${({ hideIcon }) => (hideIcon ? 'none' : 'inline-block')};
  }

  &:hover i {
    transform: ${({ iconHoverTransform, hideIcon }) =>
      hideIcon ? 'none' : iconHoverTransform || 'scale(1.1)'};
  }

  strong {
    color: ${({ strongColor }) => strongColor || '#1e293b'};
    font-weight: 600;
  }

  /* For icon only mode */
  ${({ iconOnly, noBackground }) =>
    iconOnly &&
    css`
      padding: 4px 8px;
      gap: 0;
      background: ${noBackground ? 'transparent' : 'transparent'};
      border: none;
      box-shadow: none;
      border-radius: 4px;
      color: ${({ color }) => color || '#64748b'};

      &:hover {
        background: ${noBackground ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.05)'};
      }

      i {
        font-size: ${({ iconSize }) => iconSize || '18px'};
        margin-right: 0;
        display: ${({ hideIcon }) => (hideIcon ? 'none' : 'inline-block')};
      }
    `}

  /* For toast-like notification */
  ${({ toast, noBackground }) =>
    toast &&
    css`
      border-radius: 8px;
      padding: 14px 20px;
      box-shadow: ${noBackground ? 'none' : '0 4px 20px rgba(0, 0, 0, 0.08)'};
      border: ${noBackground ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'};
      backdrop-filter: ${noBackground ? 'none' : 'blur(10px)'};
      -webkit-backdrop-filter: ${noBackground ? 'none' : 'blur(10px)'};
      background: ${({ bg, noBackground }) =>
        noBackground ? 'transparent' : bg || 'rgba(255, 255, 255, 0.9)'};
    `}

  /* For inline help text */
  ${({ inline, noBackground }) =>
    inline &&
    css`
      display: inline-flex;
      padding: ${noBackground ? '2px 0' : '2px 10px'};
      margin-top: 0;
      border-radius: ${noBackground ? '0' : '4px'};
      font-size: 12px;
      background: ${noBackground ? 'transparent' : bg || '#f1f5f9'};
    `}
`;

export const HelpText = ({
  children,
  variant = 'default',
  size = 'md',
  icon,
  iconColor,
  iconSize,
  iconMarginRight,
  marginTop,
  padding,
  bg,
  rounded,
  fontSize,
  color,
  hoverBg,
  hoverTransform,
  hoverShadow,
  border,
  showLeftBorder,
  leftBorderWidth,
  leftBorderColor,
  shadow,
  strongColor,
  iconHoverTransform,
  align,
  gap,
  cursor,
  lineHeight,
  fontWeight,
  className,
  iconOnly,
  toast,
  inline,
  noShrink,
  width,
  maxWidth,
  minWidth,
  noBackground = false,
  hideIcon = false,
  ...props
}) => {
  // Get variant styles
  const variantStyles = VARIANTS[variant] || VARIANTS.default;
  const sizeStyles = SIZES[size] || SIZES.md;

  // If noBackground is true, override background-related props
  const finalBg = noBackground ? 'transparent' : bg || variantStyles.bg;
  const finalHoverBg = noBackground
    ? 'transparent'
    : hoverBg || variantStyles.hoverBg;
  const finalBorder = noBackground ? 'none' : border || variantStyles.border;
  const finalShadow = noBackground ? 'none' : shadow || variantStyles.shadow;
  const finalHoverShadow = noBackground
    ? 'none'
    : hoverShadow || variantStyles.hoverShadow;

  // Determine if icon should be shown
  const showIcon = !hideIcon && (icon || variantStyles.icon);

  return (
    <HelpTextWrapper
      bg={finalBg}
      hoverBg={finalHoverBg}
      color={color || variantStyles.color}
      iconColor={iconColor || variantStyles.iconColor}
      border={finalBorder}
      shadow={finalShadow}
      hoverShadow={finalHoverShadow}
      padding={noBackground ? '6px 0' : padding || sizeStyles.padding}
      fontSize={fontSize || sizeStyles.fontSize}
      iconSize={iconSize || sizeStyles.iconSize}
      iconMarginRight={iconMarginRight}
      marginTop={marginTop || sizeStyles.marginTop}
      rounded={rounded || sizeStyles.rounded || '6px'}
      align={align}
      gap={gap}
      cursor={cursor}
      lineHeight={lineHeight}
      fontWeight={fontWeight}
      showLeftBorder={showLeftBorder && !noBackground}
      leftBorderWidth={leftBorderWidth}
      leftBorderColor={leftBorderColor}
      strongColor={strongColor}
      iconHoverTransform={iconHoverTransform}
      iconOnly={iconOnly}
      toast={toast}
      inline={inline}
      noShrink={noShrink}
      noBackground={noBackground}
      hideIcon={hideIcon}
      hoverTransform={hoverTransform || 'none'}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
      className={className}
      {...props}
    >
      {showIcon && <i className={icon || variantStyles.icon} />}
      <span>{children}</span>
    </HelpTextWrapper>
  );
};

// Predefined variant components for convenience
export const InfoHelpText = props => <HelpText variant="info" {...props} />;
export const SuccessHelpText = props => (
  <HelpText variant="success" {...props} />
);
export const WarningHelpText = props => (
  <HelpText variant="warning" {...props} />
);
export const DangerHelpText = props => <HelpText variant="danger" {...props} />;
export const DarkHelpText = props => <HelpText variant="dark" {...props} />;
export const PurpleHelpText = props => <HelpText variant="purple" {...props} />;
export const CleanHelpText = props => <HelpText noBackground {...props} />;
export const NoIconHelpText = props => <HelpText hideIcon {...props} />;

export default HelpText;
